import { Component, ErrorInfo, ReactNode } from 'react';
import { errorTracking } from '../lib/errorTracking';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });

    // Track error with error tracking service
    errorTracking.captureException(error, {
      page: window.location.pathname,
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-luna-bg-light dark:bg-luna-bg-dark">
          <div className="max-w-2xl w-full bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-8">
            <div className="flex items-center gap-4 mb-6">
              <span className="material-symbols-outlined text-4xl text-red-500">error</span>
              <h1 className="text-2xl font-bold text-luna-text-light dark:text-luna-text-dark">
                Something went wrong
              </h1>
            </div>
            
            <p className="text-luna-text-light/70 dark:text-luna-text-muted-dark mb-6">
              We're sorry, but something unexpected happened. Please try refreshing the page or contact support if the problem persists.
            </p>

            {this.state.error && (
              <details className="mb-6">
                <summary className="cursor-pointer text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80 hover:text-luna-secondary transition-colors mb-2">
                  Error Details
                </summary>
                <div className="mt-2 p-4 bg-luna-bg-light dark:bg-luna-bg-dark rounded-lg border border-luna-text-light/10 dark:border-luna-text-dark/20">
                  <p className="text-xs font-mono text-red-600 dark:text-red-400 mb-2">
                    {this.state.error.toString()}
                  </p>
                  {this.state.errorInfo && (
                    <pre className="text-xs font-mono text-luna-text-light/60 dark:text-luna-text-muted-dark overflow-auto">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            <div className="flex gap-4">
              <button
                onClick={this.handleReset}
                className="flex items-center justify-center gap-2 h-10 px-6 bg-luna-secondary text-luna-primary text-sm font-bold rounded-lg hover:opacity-90 transition-opacity"
              >
                <span className="material-symbols-outlined text-base">refresh</span>
                Try Again
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="flex items-center justify-center gap-2 h-10 px-6 bg-luna-bg-light dark:bg-luna-bg-dark border border-luna-text-light/20 dark:border-luna-text-dark/30 text-sm font-medium rounded-lg hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10 transition-colors"
              >
                <span className="material-symbols-outlined text-base">home</span>
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

